import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { PricingActions } from 'state/modules/pricing';
import { Box, Grid, Paper, Button, Container, Typography } from '@material-ui/core';
import i18n from 'i18n-js';
import { buildUrl } from 'utils';
import Layout from 'components/Layout';
import useStyles from './RulesetCreate.styled';
import Title from 'components/Title';
import { Formik, Field } from 'formik';
import * as Yup from 'yup';
import { useParams } from 'react-router-dom';
import { useConfirm } from 'hooks';
import { ReservationsActions } from 'state/modules/reservations';
import { Rulesets } from 'components/Ruleset';

import { TextField } from 'components/FormFields';

const RulesetCreate = ({ history }) => {
  const classes = useStyles();

  const dispatch = useDispatch();
  const formRef = useRef(null);
  const { id, action } = useParams();
  const [firstLoad, setFirstLoad] = useState(true);

  const {
    fetchingCreateRuleset,
    fetchedCreateRuleset,
    fetchedUpdateRuleset,
    currentId,
    fetchingUpdateRuleset,
    current: { name, rules, group_type },
  } = useSelector((state) => state.pricing);

  const sharingType = useSelector(
    (state) => state.config?.settings?.core?.partnership?.sharing_type
  );

  const { rules: rules_set } = useSelector((state) => state.reservations);

  useEffect(() => {
    if (fetchedCreateRuleset || fetchedUpdateRuleset) {
      history.push(buildUrl(`/pricing-rulesets/${currentId}`));
      dispatch(PricingActions.resetCurrentRuleset());
      dispatch(PricingActions.resetOnboarding());
    }
  }, [fetchedCreateRuleset, fetchedUpdateRuleset]); // eslint-disable-line

  useEffect(() => {
    if (id && action && !name) {
      dispatch(PricingActions.fetchRuleset(id));
    }
  }, [id, action]); // eslint-disable-line

  const initialValues = {
    name: name || '',
    group_type: group_type || 'VEHICLE',
    rules: rules || {},
  };

  useEffect(() => {
    if (name && action === 'clone' && firstLoad) {
      dispatch(
        PricingActions.fetchUpdateRulesetInfo({
          name: `Copy of ${name}` || '',
        })
      );
      setFirstLoad(false);
    }
  }, [name]); // eslint-disable-line

  useEffect(() => {
    if (rules_set.length === 0) {
      dispatch(ReservationsActions.fetchReservationRules({ is_enabled: true }));
    }
  }, [dispatch]); // eslint-disable-line

  const formSchema = Yup.object().shape({
    name: Yup.string().required(i18n.t('common.required_field')),
    group_type: Yup.string(),
    rules: Yup.array(),
  });

  const onSubmit = (values) => {
    const data = { ...values };

    // Remove empty tiers
    data.rules = data.rules.map((rule) => {
      if (rule?.extra_arguments?.tiers && Object.keys(rule?.extra_arguments?.tiers).length === 0) {
        delete rule.extra_arguments.tiers;
      }
      return rule;
    });

    if (action === 'update' && id) {
      dispatch(PricingActions.fetchUpdateRuleset(id, { ...data }));
    } else {
      dispatch(PricingActions.fetchCreateRuleset(data));
    }
  };

  const onDelete = (index) => {
    dispatch(PricingActions.fetchRemoveRule(index));
  };

  const onUpdate = (data) => {
    dispatch(PricingActions.fetchUpdateRules(data));
  };

  const [toggleCancel, CancelConfirm] = useConfirm({
    title: i18n.t('ruleset.cancel_title'),
    message: i18n.t('ruleset.cancel_description'),
    cancelText: i18n.t('common.cancel'),
    confirmText: i18n.t('common.leave_page'),
    onConfirm: () => {
      dispatch(PricingActions.resetCurrentRuleset());
      dispatch(PricingActions.resetOnboarding());
      history.push(buildUrl('/pricing-rulesets'));
    },
  });

  const getTitle = () => {
    if (action === 'update' && id) {
      return i18n.t('ruleset.edit_ruleset');
    }
    if (action === 'clone') {
      return i18n.t('ruleset.clone_ruleset');
    }
    return i18n.t('ruleset.ruleset_create');
  };

  return (
    <Layout title={i18n.t('ruleset.title')}>
      <Container className={classes.container}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Grid container spacing={2} direction="row" alignItems="center" justifyContent="space-between">
              <Grid item>
                <Title title={getTitle()} />
              </Grid>
              <Grid item>
                <Button
                  onClick={toggleCancel}
                  style={{ textTransform: 'capitalize', marginRight: '1rem' }}
                  variant="outlined"
                  disabled={fetchingCreateRuleset || fetchingUpdateRuleset}
                  color="primary"
                >
                  {i18n.t('common.cancel')}
                </Button>
                <Button
                  onClick={() => formRef.current.submitForm()}
                  style={{ textTransform: 'capitalize' }}
                  variant="contained"
                  disabled={fetchingCreateRuleset || fetchingUpdateRuleset}
                  color="primary"
                >
                  {i18n.t('common.save')}
                </Button>
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Paper elevation={2} className={classes.paper}>
                  <Grid item xs={12}>
                    <Typography variant="h6" align="left">
                      <Box fontWeight="normal" fontSize={13}>
                        {sharingType === 'FFB'
                          ? i18n.t('ruleset.description_1_fleet')
                          : i18n.t('ruleset.description_1')}
                      </Box>
                    </Typography>
                    <Typography variant="h6" align="left">
                      <Box fontWeight="normal" fontSize={13} mt={1} style={{ height: '100%' }}>
                        {sharingType === 'FFB'
                          ? i18n.t('ruleset.description_2_fleet')
                          : i18n.t('ruleset.description_2')}
                      </Box>
                    </Typography>
                  </Grid>

                  <Grid item xs={12}>
                    <Formik
                      initialValues={initialValues}
                      validationSchema={formSchema}
                      enableReinitialize={true}
                      innerRef={formRef}
                      onSubmit={onSubmit}
                    >
                      {({ handleSubmit, errors, values }) => (
                        <form noValidate onSubmit={handleSubmit}>
                          <Grid container spacing={1} alignItems="flex-end" justifyContent="space-between">
                            <Grid item xs={4} style={{ marginTop: 15 }}>
                              <Field
                                margin="none"
                                label={i18n.t('ruleset.ruleset_name')}
                                name="name"
                                onKeyUp={(val) =>
                                  dispatch(
                                    PricingActions.fetchUpdateRulesetInfo({
                                      name: values.name || '',
                                    })
                                  )
                                }
                                component={TextField}
                              />
                            </Grid>
                          </Grid>
                        </form>
                      )}
                    </Formik>
                  </Grid>

                  <Grid item xs={12} style={{ marginTop: 25 }}>
                    <Rulesets
                      onDelete={onDelete}
                      onUpdate={onUpdate}
                      id={id}
                      action={action}
                      rules={rules || []}
                    />
                  </Grid>
                </Paper>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
        <CancelConfirm />
      </Container>
    </Layout>
  );
};

export default withRouter(RulesetCreate);
